import React, { useState } from 'react';
import {
  View, Text, FlatList, Alert,
} from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card, Title, Paragraph, Button, Dialog, Portal, TextInput,
  Appbar, Badge,
  List, Menu,
} from 'react-native-paper';
import LoadingIndicator from '../../components/commons/LoadingIndicator';
import {
  GET_EQUIPMENTS,
} from '../../gql/equipment/query';
import { CREATE_EQUIPMENT, DELETE_EQUIPMENT, UPDATE_EQUIPMENT } from '../../gql/equipment/mutation';
import { styles } from './EquipmentsTab.style';

function EquipmentsTab() {
  const {
    data, loading, error, refetch,
  } = useQuery(GET_EQUIPMENTS);

  const [createEquipment] = useMutation(CREATE_EQUIPMENT);
  const [updateEquipment] = useMutation(UPDATE_EQUIPMENT);
  const [deleteEquipment] = useMutation(DELETE_EQUIPMENT);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [currentEquipment, setCurrentEquipment] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [size, setSize] = useState('');
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [equipmentToDeleteId, setEquipmentToDeleteId] = useState(null);
  const [sizeMenuVisible, setSizeMenuVisible] = useState(false);

  const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  const showDialog = (equipment = null) => {
    setCurrentEquipment(equipment);
    setName(equipment ? equipment.name : '');
    setDescription(equipment ? equipment.description : '');
    setSize(equipment ? equipment.size : '');
    setDialogVisible(true);
  };

  const hideDialog = () => setDialogVisible(false);
  const showDeleteDialog = (id) => {
    setEquipmentToDeleteId(id);
    setDeleteDialogVisible(true);
  };
  const hideDeleteDialog = () => {
    setEquipmentToDeleteId(null);
    setDeleteDialogVisible(false);
  };

  const handleSave = async () => {
    try {
      if (currentEquipment) {
        await updateEquipment({
          variables: {
            id: currentEquipment.id,
            name,
            description,
            size,
          },
        });
      } else {
        await createEquipment({
          variables: {
            name,
            description,
            size,
          },
        });
      }
      refetch();
      hideDialog();
    } catch (e) {
      Alert.alert('Error', e.message);
    }
  };

  const handleDelete = (id) => {
    showDeleteDialog(id);
  };

  const confirmDelete = async () => {
    try {
      await deleteEquipment({ variables: { id: equipmentToDeleteId } });
      refetch();
      hideDeleteDialog();
    } catch (e) {
      Alert.alert('Error', e.message);
    }
  };

  if (loading) return <LoadingIndicator text="Chargement des équipements..." />;
  if (error) {
    return (
      <Text>
        Error:
        {error.message}
      </Text>
    );
  }

  const renderEquipmentItem = ({ item }) => (
    <Card style={styles.card}>
      <Card.Content>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Title>{item.name}</Title>
          {item.bags && item.bags.length > 0 && (
            <Badge style={{ marginLeft: 8, alignSelf: 'center' }}>{item.bags.length}</Badge>
          )}
        </View>
        <Paragraph>{item.description}</Paragraph>
        <Paragraph>
          Taille:
          {' '}
          {item.size}
        </Paragraph>
        {item.bags && item.bags.length > 0 && (
          <View style={styles.bagInfo}>
            <Text>Assigné aux sacs:</Text>
            {item.bags.map((equipmentOnBag) => (
              <Text key={equipmentOnBag.bag.id}>
                -
                {' '}
                {equipmentOnBag.bag.name || `Sac ${equipmentOnBag.bag.id}`}
              </Text>
            ))}
          </View>
        )}
      </Card.Content>
      <Card.Actions>
        <Button onPress={() => showDialog(item)}>Modifier</Button>
        <Button onPress={() => handleDelete(item.id)} disabled={item.bags && item.bags.length > 0}>Supprimer</Button>
      </Card.Actions>
    </Card>
  );

  return (
    <View style={styles.container}>
      <Appbar.Header statusBarHeight={0}>
        <Appbar.Content title="Liste des equipements" />
        <Appbar.Action
          icon="plus-box"
          onPress={() => showDialog()}
        />
      </Appbar.Header>
      <FlatList
        data={data.equipments}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderEquipmentItem}
      />

      <Portal>
        <Dialog visible={dialogVisible} onDismiss={hideDialog}>
          <Dialog.Title>{currentEquipment ? 'Modifier Equipment' : 'Ajouter Equipment'}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Nom"
              value={name}
              onChangeText={setName}
              style={styles.input}
            />
            <TextInput
              label="Description"
              value={description}
              onChangeText={setDescription}
              style={styles.input}
            />
            <Menu
              visible={sizeMenuVisible}
              onDismiss={() => setSizeMenuVisible(false)}
              anchor={(
                <TextInput
                  label="Taille"
                  value={size}
                  editable={false}
                  onPress={() => setSizeMenuVisible(true)}
                  right={<TextInput.Icon icon="menu-down" onPress={() => setSizeMenuVisible(true)} />}
                  style={styles.input}
                />
              )}
            >
              {sizeOptions.map((option) => (
                <Menu.Item
                  key={option}
                  onPress={() => {
                    setSize(option);
                    setSizeMenuVisible(false);
                  }}
                  title={option}
                />
              ))}
            </Menu>
            {currentEquipment && currentEquipment.bags && currentEquipment.bags.length > 0 && (
              <List.Section title="Assigné aux sacs:">
                {currentEquipment.bags.map((equipmentOnBag) => (
                  <List.Item
                    key={equipmentOnBag.bag.id.toString()}
                    title={equipmentOnBag.bag.name || `Sac ${equipmentOnBag.bag.id}`}
                  />
                ))}
              </List.Section>
            )}
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={hideDialog}>Annuler</Button>
            <Button onPress={handleSave}>Enregistrer</Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog visible={deleteDialogVisible} onDismiss={hideDeleteDialog}>
          <Dialog.Title>Supprimer Equipment</Dialog.Title>
          <Dialog.Content>
            <Paragraph>Etes vous certain de vouloir supprimer cet equipment ?</Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={hideDeleteDialog}>Annuler</Button>
            <Button onPress={confirmDelete}>Supprimer</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}

export default EquipmentsTab;
